import { Icon } from '@iconify/react';
import { HttpStatusCode } from 'axios';
import classNames from 'classnames';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, BadgeVariantProps } from '@/Domain/components/Shared/Badge/Badge';
import { PrettyJsonViewer } from '@/Domain/components/Shared/JsonViewier/JsonViewier';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type Props = {
  data: any; // Data from external API - just to display the results
  httpCode?: HttpStatusCode;
  bashCurlCommand?: string;
};

const getBadgeConfig = (
  statusCode: number | undefined,
): { variant: BadgeVariantProps['variant']; label: string; icon: string } | null => {
  if (!statusCode) {
    return null;
  }

  if (statusCode >= 400 && statusCode <= 511) {
    return {
      variant: 'error',
      label: `${statusCode} ${HttpStatusCode[statusCode]}`,
      icon: 'mingcute:warning-fill',
    };
  }

  if (statusCode >= 200 && statusCode < 300) {
    return {
      variant: 'success',
      label: `${statusCode} ${HttpStatusCode[statusCode]}`,
      icon: 'mingcute:check-circle-fill',
    };
  }

  return null;
};

export const Response = ({ data, httpCode, bashCurlCommand }: Props) => {
  const { t } = useTranslation('actions_page');
  const badge = getBadgeConfig(httpCode);
  const [curlExpanded, setCurlExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!bashCurlCommand) return;
    await navigator.clipboard.writeText(bashCurlCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (data) {
    return (
      <div className="space-y-3">
        <div className={classNames('relative border rounded-lg w-full flex overflow-y-auto')}>
          <section className="flex justify-between items-center absolute top-3 right-3">
            {badge && (
              <Badge variant={badge.variant}>
                <Icon icon={badge.icon} className="pr-1.5" />
                {badge.label}
              </Badge>
            )}
          </section>

          <PrettyJsonViewer data={data} />
        </div>

        {bashCurlCommand && (
          <div className="border rounded-lg overflow-hidden">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setCurlExpanded(!curlExpanded)}
                    className="flex items-center gap-1.5 w-full px-3 py-2 text-sm text-muted-foreground hover:bg-accent transition-colors"
                  >
                    {curlExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    {curlExpanded
                      ? t('sheet.form.test_response.curl.hide')
                      : t('sheet.form.test_response.curl.show')}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {t('sheet.form.test_response.curl.tooltip')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {curlExpanded && (
              <div className="relative border-t">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="absolute top-2 right-2 p-1.5 rounded hover:bg-accent text-foreground-subtle transition-colors"
                  title={
                    copied
                      ? t('sheet.form.test_response.curl.copied')
                      : t('sheet.form.test_response.curl.copy')
                  }
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <pre className="p-3 pr-10 text-xs bg-muted overflow-x-auto whitespace-pre-wrap break-all font-mono">
                  {bashCurlCommand}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};
